"use client";

import { useState, useEffect } from "react";
import { addWeeks, subWeeks } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Button } from "../button.js";
import { Input } from "../input.js";
import { Label } from "../label.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select.js";
import { LoadingSpinner } from "../loading-spinner.js";
import { Calendar, Search, Clock, Users, MapPin } from "lucide-react";
import { useBookingAvailability } from "../../hooks/booking/useBookingAvailability.js";
import { useBookingSubmission } from "../../hooks/booking/useBookingSubmission.js";
import { WeekNavigation } from "./WeekNavigation.js";
import { AvailabilityGrid } from "./AvailabilityGrid.js";
import { BookingForm } from "./BookingForm.js";
import { BookingSuccess } from "./BookingSuccess.js";

interface BookingConfig {
  mode: 'public' | 'internal';
  features: {
    showPersonSelector: boolean;
    showDurationSelector: boolean;
    allowCustomTimes: boolean;
    requireAuthentication: boolean;
  };
  ui: {
    theme: 'calendly' | 'internal';
    showWeekNavigation: boolean;
    showTimeZoneSelector: boolean;
  };
}

interface UnifiedBookingPageProps {
  config: BookingConfig;
  bookeeEmail?: string;
  groupEmails?: string[];
  eventType?: string;
  eventTypeName?: string;
  eventTypeDescription?: string;
  meetingDuration?: number;
  presetDurations?: number[];
  selectedSlot?: string | null;
  prefilledName?: string;
  prefilledEmail?: string;
  defaultLocation?: string;
  isLocationEditable?: boolean;
  isOnline?: boolean;
  onEmailChange?: (email: string) => void;
  onDurationChange?: (duration: number) => void;
  onSlotSelect?: (slot: string | null) => void;
  onBookingSuccess?: (eventId: string) => void;
}

export function UnifiedBookingPage({ 
  config, 
  bookeeEmail: initialBookeeEmail,
  groupEmails,
  eventType,
  eventTypeName,
  eventTypeDescription,
  meetingDuration: initialMeetingDuration,
  presetDurations,
  selectedSlot: initialSelectedSlot,
  prefilledName,
  prefilledEmail,
  defaultLocation,
  isLocationEditable = true,
  isOnline = false,
  onEmailChange,
  onDurationChange,
  onSlotSelect,
  onBookingSuccess 
}: UnifiedBookingPageProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<string | null>(initialSelectedSlot || null);
  const [bookeeEmail, setBookeeEmail] = useState(initialBookeeEmail || "");
  const [meetingDuration, setMeetingDuration] = useState(
    initialMeetingDuration || (presetDurations && presetDurations.length > 0 ? presetDurations[0] : 30)
  );
  const [showAvailability, setShowAvailability] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [eventId, setEventId] = useState<string | undefined>();

  // Determine which emails to use for availability
  const emailsForAvailability = eventType && groupEmails && groupEmails.length > 0
    ? groupEmails
    : (bookeeEmail ? [bookeeEmail] : []);

  const { availability, isLoading, error, refetch } = useBookingAvailability({
    bookeeEmail: eventType ? undefined : bookeeEmail,
    groupEmails: eventType ? groupEmails : undefined,
    selectedWeek: currentDate,
    meetingDuration,
    eventType: eventType || undefined, // Pass event type for minimum date calculation
    enabled: emailsForAvailability.length > 0 && !!meetingDuration,
  });

  const { isSubmitting, submitBooking } = useBookingSubmission();

  // Auto-show availability when data is available
  useEffect(() => {
    if (availability && availability.length > 0) {
      setShowAvailability(true);
    }
  }, [availability]);

  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleSlotSelect = (slot: { start: string; end: string }) => {
    setSelectedSlot(slot.start);
    onSlotSelect?.(slot.start);
  };

  const handleBookingSubmit = async (data: any) => {
    // Add eventType to booking data if in event type mode
    const bookingData = eventType 
      ? { ...data, eventType }
      : data;
    
    const result = await submitBooking(bookingData);
    if (result.success) {
      setBookingSuccess(true);
      setEventId(result.eventId);
      if (onBookingSuccess && result.eventId) {
        onBookingSuccess(result.eventId);
      }
    }
  };

  const handleReset = () => {
    setBookingSuccess(false);
    setSelectedSlot(null);
    setEventId(undefined);
  };

  // Reset availability when email or duration changes
  useEffect(() => {
    if (showAvailability) {
      setShowAvailability(false);
      setSelectedSlot(null);
    }
  }, [bookeeEmail, groupEmails, meetingDuration, eventType]);

  // Show success state
  if (bookingSuccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BookingSuccess onReset={handleReset} eventId={eventId} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <div className="text-center mb-6 sm:mb-8">
        {eventType && eventTypeName ? (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-card-foreground">
              {eventTypeName}
            </h1>
            {eventTypeDescription && (
              <p className="text-sm sm:text-base text-gray-600 mb-4 max-w-2xl mx-auto">
                {eventTypeDescription}
              </p>
            )}
            <p className="text-xs sm:text-sm text-gray-500 mb-4 max-w-2xl mx-auto">
              Select your preferred time and we'll send you a calendar invitation
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-card-foreground">Book a Meeting</h1>
            <p className="text-sm sm:text-base text-gray-600 mb-4 max-w-2xl mx-auto">
              {config.mode === 'public' 
                ? "Select your preferred time and we'll send you a calendar invitation"
                : "Select the person you want to book with and their availability will be shown automatically"
              }
            </p>
          </>
        )}
      </div>

      {/* Email Input and Duration Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            {config.features.showPersonSelector ? "Select Person & Duration" : "Meeting Details"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Only show person selector if NOT in event type mode */}
            {config.features.showPersonSelector && !eventType && (
              <div>
                <Label htmlFor="bookeeEmail">Person to Book With *</Label>
                <Select value={bookeeEmail} onValueChange={(value: string) => {
                  setBookeeEmail(value);
                  onEmailChange?.(value);
                }}>
                  <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue placeholder="Select person to book with" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                    <SelectItem value="eliza.keffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Eliza Keffeler</SelectItem>
                    <SelectItem value="charlie.keffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Charlie Keffeler</SelectItem>
                    <SelectItem value="oliviakeffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Olivia Keffeler</SelectItem>
                    <SelectItem value="jimmytreb@gmail.com" className="hover:bg-card-50 focus:bg-card-50">Jimmy Turner</SelectItem>
                    {bookeeEmail === "miclainkeffeler@westtn.consulting" && (
                      <SelectItem value="miclainkeffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Miclain Keffeler</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show event type info when in event type mode */}
            {eventType && (
              <div className="sm:col-span-2 lg:col-span-1">
                <Label>Meeting Type</Label>
                <div className="mt-1 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
                    {eventTypeName || 'Scheduled Meeting'}
                  </div>
                  {groupEmails && groupEmails.length > 0 && (
                    <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {groupEmails.length} team member{groupEmails.length !== 1 ? 's' : ''} available
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {config.features.showDurationSelector && (
              <div>
                <Label htmlFor="meetingDuration">Meeting Duration</Label>
                <Select value={meetingDuration.toString()} onValueChange={(value: string) => {
                  const duration = parseInt(value);
                  setMeetingDuration(duration);
                  onDurationChange?.(duration);
                }}>
                  <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                    {presetDurations && presetDurations.length > 0 ? (
                      presetDurations.map((duration) => (
                        <SelectItem key={duration} value={duration.toString()} className="hover:bg-card-50 focus:bg-card-50">
                          {duration} minutes
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="15" className="hover:bg-card-50 focus:bg-card-50">15 minutes</SelectItem>
                        <SelectItem value="30" className="hover:bg-card-50 focus:bg-card-50">30 minutes</SelectItem>
                        <SelectItem value="45" className="hover:bg-card-50 focus:bg-card-50">45 minutes</SelectItem>
                        <SelectItem value="60" className="hover:bg-card-50 focus:bg-card-50">1 hour</SelectItem>
                        <SelectItem value="90" className="hover:bg-card-50 focus:bg-card-50">1.5 hours</SelectItem>
                        <SelectItem value="120" className="hover:bg-card-50 focus:bg-card-50">2 hours</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <Button 
                onClick={() => refetch()}
                disabled={emailsForAvailability.length === 0 || isLoading}
                variant="outline"
                className="w-full h-10 sm:h-11"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Loading...</span>
                    <span className="sm:hidden">Loading</span>
                  </>
                ) : (
                  <>
                    <Calendar className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Refresh Availability</span>
                    <span className="sm:hidden">Refresh</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {emailsForAvailability.length > 0 && error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-600 mb-2">
                Error Loading Availability
              </h3>
              <p className="text-red-600">
                {error?.message || "Failed to load calendar availability"}
              </p>
              <p className="text-sm text-red-500 mt-2">
                Make sure the email address is correct and the person has connected their calendar.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week Navigation */}
      {showAvailability && availability && availability.length > 0 && (
        <WeekNavigation
          currentDate={currentDate}
          onPreviousWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onGoToToday={goToToday}
          showNavigation={config.ui.showWeekNavigation}
        />
      )}

      {/* Availability and Booking Section */}
      {showAvailability && availability && availability.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          {/* Availability Grid */}
          <Card>
            <CardContent className="p-3 sm:p-6">
              <AvailabilityGrid
                availableDays={availability || []}
                selectedSlot={selectedSlot}
                onSlotSelect={handleSlotSelect}
                bookeeEmail={bookeeEmail}
              />
            </CardContent>
          </Card>

          {/* Booking Form */}
          <BookingForm
            selectedSlot={selectedSlot}
            bookeeEmail={bookeeEmail || (groupEmails && groupEmails.length > 0 ? groupEmails[0] : "")}
            onSubmit={handleBookingSubmit}
            isLoading={isSubmitting}
            isPublic={config.mode === 'public'}
            prefilledName={prefilledName}
            prefilledEmail={prefilledEmail}
            prefilledLocation={defaultLocation}
            isLocationEditable={isLocationEditable}
            isOnline={isOnline}
          />
        </div>
      )}

      {/* Initial State */}
      {(emailsForAvailability.length === 0 || (!showAvailability && !error)) && (
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="text-center py-6 sm:py-8">
              <Calendar className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2 text-card-foreground">Ready to Book a Meeting?</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 max-w-2xl mx-auto">
                {config.mode === 'public' 
                  ? "Select your preferred meeting duration above and available time slots will be displayed automatically."
                  : "Select the person you want to book with above and choose your preferred meeting duration. Their available time slots will be displayed automatically."
                }
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center justify-center gap-1">
                  <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{config.features.showPersonSelector ? "Select person" : "Choose duration"}</span>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Choose time slot</span>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Book meeting</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
